import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Alert,
  Chip,
  Paper,
  Divider
} from '@mui/material';
import {
  Close as CloseIcon,
  Check as CheckIcon,
  Percent as PercentIcon,
  CardGiftcard as GiftIcon
} from '@mui/icons-material';
import { API_BASE_URL } from '../config/api';

const CouponInput = ({ 
  cartItems = [], 
  onCouponApplied, 
  onCouponRemoved, 
  userId = null,
  isGiftShop = false,
  disabled = false 
}) => {
  const [couponCode, setCouponCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);

  const validateCoupon = async () => {
    if (!couponCode.trim()) {
      setError('يرجى إدخال كود الخصم');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/coupons.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        // Remove credentials for now to avoid CORS issues
        // credentials: 'include',
        body: JSON.stringify({
          coupon_code: couponCode.trim().toUpperCase(),
          cart_items: cartItems,
          user_id: userId,
          is_giftshop: isGiftShop
        })
      });

      const data = await response.json();

      if (data.success) {
        setAppliedCoupon({
          code: data.coupon.code,
          discount: data.discount,
          couponId: data.coupon.id
        });
        setCouponCode('');
        
        // Notify parent component
        if (onCouponApplied) {
          onCouponApplied({
            coupon: data.coupon,
            discount: data.discount
          });
        }
      } else {
        setError(data.error || 'حدث خطأ أثناء التحقق من كود الخصم');
      }
    } catch (error) {
      console.error('Coupon validation error:', error);
      setError('حدث خطأ في الاتصال، يرجى المحاولة مرة أخرى');
    } finally {
      setLoading(false);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    setError('');
    
    // Notify parent component
    if (onCouponRemoved) {
      onCouponRemoved();
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      validateCoupon();
    }
  };

  // If coupon is applied, show the applied coupon chip
  if (appliedCoupon) {
    return (
      <Paper 
        elevation={2}
        sx={{ 
          mb: 3, 
          borderRadius: 4,
          overflow: 'hidden',
          background: 'linear-gradient(135deg, #e8f5e8 0%, #f1f8e9 100%)',
          border: '2px solid #4caf50',
          position: 'relative'
        }}
      >
        {/* Success background pattern */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: 80,
            height: 80,
            background: 'radial-gradient(circle, rgba(76, 175, 80, 0.1) 0%, transparent 60%)',
          }}
        />
        
        <Box sx={{ p: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)'
              }}
            >
              <CheckIcon fontSize="medium" />
            </Box>
            
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#2e7d32', mb: 0.5 }}>
                تم تطبيق كود الخصم بنجاح
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Chip
                  label={appliedCoupon.code}
                  size="medium"
                  sx={{
                    background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    height: 32,
                    '& .MuiChip-label': {
                      px: 1.5
                    }
                  }}
                />
                <Typography variant="body1" sx={{ 
                  color: '#2e7d32', 
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5
                }}>
                  <PercentIcon fontSize="small" />
                  خصم {appliedCoupon.discount.discount_type === 'percent' 
                    ? `${appliedCoupon.discount.discount_value}%`
                    : `${appliedCoupon.discount.discount_amount.toLocaleString()} د.ع`
                  }
                </Typography>
              </Box>
            </Box>
          </Box>
          
          {!disabled && (
            <Button
              onClick={removeCoupon}
              size="small"
              sx={{
                backgroundColor: 'rgba(244, 67, 54, 0.1)',
                color: '#f44336',
                minWidth: 40,
                width: 40,
                height: 40,
                p: 0,
                '&:hover': {
                  backgroundColor: 'rgba(244, 67, 54, 0.2)',
                  transform: 'rotate(90deg)'
                },
                transition: 'all 0.2s ease'
              }}
            >
              <CloseIcon fontSize="small" />
            </Button>
          )}
        </Box>
      </Paper>
    );
  }

  return (
    <Box sx={{ mb: 3 }}>
      {/* Coupon Input - Always Visible */}
      <Paper
        elevation={0}
        sx={{ 
          borderRadius: 3,
          overflow: 'hidden',
          background: '#ffffff',
          border: '1px solid #e3f2fd'
        }}
      >
        {/* Content */}
        <Box sx={{ p: 2 }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'stretch',
            gap: 2,
            mb: error ? 2 : 0
          }}>
              {/* Code Box */}
              <Box
                sx={{
                  flexGrow: 1,
                  display: 'flex',
                  alignItems: 'center',
                  px: 3,
                  py: 2,
                  border: '2px solid',
                  borderColor: couponCode ? '#4caf50' : '#e0e0e0',
                  borderRadius: 3,
                  backgroundColor: '#fafafa',
                  fontFamily: 'monospace',
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                  fontWeight: 600,
                  letterSpacing: '2px',
                  color: couponCode ? '#2e7d32' : '#9e9e9e',
                  textAlign: 'center',
                  justifyContent: 'center',
                  minHeight: 56,
                  textTransform: 'uppercase',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'hidden',
                  cursor: 'text',
                  '&:hover': {
                    borderColor: '#1976d2',
                    backgroundColor: '#f5f5f5'
                  }
                }}
                onClick={() => {
                  const input = document.getElementById('coupon-input-hidden');
                  if (input) input.focus();
                }}
              >
                <input
                  id="coupon-input-hidden"
                  type="text"
                  value={couponCode}
                  onChange={(e) => {
                    setCouponCode(e.target.value.toUpperCase());
                    setError('');
                  }}
                  onKeyPress={handleKeyPress}
                  disabled={loading || disabled}
                  placeholder="XXXXXX"
                  maxLength={20}
                  style={{
                    width: '100%',
                    border: 'none',
                    outline: 'none',
                    background: 'transparent',
                    fontFamily: 'monospace',
                    fontSize: 'inherit',
                    fontWeight: 'inherit',
                    letterSpacing: 'inherit',
                    color: 'inherit',
                    textAlign: 'center',
                    textTransform: 'uppercase'
                  }}
                />
              </Box>
              
              {/* Apply Button */}
              <Button
                variant="contained"
                onClick={validateCoupon}
                disabled={loading || disabled || !couponCode.trim()}
                sx={{
                  minWidth: { xs: 100, sm: 120 },
                  px: { xs: 2, sm: 3 },
                  py: 2,
                  borderRadius: 3,
                  fontWeight: 700,
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  textTransform: 'none',
                  background: loading || disabled || !couponCode.trim()
                    ? '#e0e0e0'
                    : 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)',
                  color: loading || disabled || !couponCode.trim() ? '#9e9e9e' : '#ffffff',
                  boxShadow: loading || disabled || !couponCode.trim()
                    ? 'none'
                    : '0 4px 14px rgba(76, 175, 80, 0.4)',
                  border: 'none',
                  '&:hover': {
                    background: loading || disabled || !couponCode.trim()
                      ? '#e0e0e0'
                      : 'linear-gradient(135deg, #66bb6a 0%, #388e3c 100%)',
                    boxShadow: loading || disabled || !couponCode.trim()
                      ? 'none'
                      : '0 6px 20px rgba(76, 175, 80, 0.5)',
                    transform: loading || disabled || !couponCode.trim() ? 'none' : 'translateY(-2px)'
                  },
                  '&:disabled': {
                    background: '#e0e0e0',
                    color: '#9e9e9e'
                  },
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
              >
                {loading ? 'جاري...' : 'تطبيق'}
              </Button>
            </Box>

            {error && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 2,
                  borderRadius: 2,
                  backgroundColor: '#ffebee',
                  color: '#c62828',
                  '& .MuiAlert-icon': {
                    color: '#f44336'
                  }
                }}
                onClose={() => setError('')}
              >
                {error}
              </Alert>
            )}

            <Divider sx={{ my: 2, borderColor: '#e3f2fd' }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <GiftIcon sx={{ color: '#1976d2', fontSize: 18 }} />
                <Typography variant="caption" sx={{ color: '#757575', fontWeight: 500 }}>
                  أدخل الكود بالأحرف الكبيرة
                </Typography>
              </Box>
              <Typography variant="caption" sx={{ 
                color: '#4caf50', 
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5
              }}>
                ✨ وفر المزيد مع أكواد الخصم
              </Typography>
            </Box>
          </Box>
        </Paper>
    </Box>
  );
};

export default CouponInput;